import * as React from 'react';
import { DefaultFocusIndicator } from '../styles/FocusIndicators';
import { PreventUserSelect } from '../styles/PreventUserSelect';
import { TabStyles } from '../styles/TabStyles';
import { SwitcherTextBase, SwitcherTextBaseStyle } from './SwitcherTextBase';

export enum SelectedToggle {
  Left,
  Right
}

export interface ToggleTextProps {
  /** The description of the toggle that will be read by screen readers. */
  ariaLabel?: string;
  className?: string;
  /** The styling of the toggle container.  */
  containerStyle?: React.CSSProperties;
  disabled?: boolean;
  /** Called when left toggle is selected. */
  handleLeftToggleSelected: () => void;
  /** Called when right toggle is selected. */
  handleRightToggleSelected: () => void;
  /** The text of the left toggle. */
  leftToggleText: string;
  /** The `<input>` name attribute. */
  name?: string;
  /** The text of the right toggle. */
  rightToggleText: string;
  /** Which toggle is selected. */
  selectedToggle: SelectedToggle;
  /** Test id to be attached to the toggle. '-{0/1}-{label/input}' will be automatically added as a suffix */
  testId: string;
}

export const SwitcherStyles: SwitcherTextBaseStyle = {
  container: {
    display: 'flex'
  },
  toggleBase: {
    ...PreventUserSelect,
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: TabStyles.Sizing.BorderWidth,
    display: 'flex',
    flexBasis: '100%',
    justifyContent: 'center',
    lineHeight: (TabStyles.Sizing.RowHeight - TabStyles.Sizing.BorderWidth * 2) + 'px',
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
  },
  selectedToggle: {
    backgroundColor: TabStyles.Colors.F8,
    borderColor: TabStyles.Colors.F8,
    color: TabStyles.Typography.Color.InvertedPrimary,
  },
  unselectedToggle: {
    backgroundColor: TabStyles.Colors.F1,
    borderColor: TabStyles.Colors.L6,
    color: TabStyles.Typography.Color.Primary,
  },
  hoverSelectedToggle: {
    borderColor: TabStyles.Colors.L9
  },
  hoverUnselectedToggle: {
    borderColor: TabStyles.Colors.L9
  },
  focusSelectedToggle: {
    borderColor: DefaultFocusIndicator.borderColor as string,
    boxShadow: DefaultFocusIndicator.boxShadow as string,
    position: 'relative',
  },
  focusUnselectedToggle: {
    borderColor: DefaultFocusIndicator.borderColor as string,
    boxShadow: DefaultFocusIndicator.boxShadow as string,
    position: 'relative',
  },
  activeSelectedToggle: {
    backgroundColor: TabStyles.Colors.F8,
    borderColor: TabStyles.Colors.L9,
    color: TabStyles.Typography.Color.InvertedPrimary,
  },
  activeUnselectedToggle: {
    backgroundColor: TabStyles.Colors.F8,
    borderColor: TabStyles.Colors.L9,
    color: TabStyles.Typography.Color.InvertedPrimary,
  },
  inactiveToggle: {
    backgroundColor: TabStyles.Colors.F1,
    borderColor: TabStyles.Colors.L6,
    color: TabStyles.Typography.Color.Primary,
  },
  disabledSelectedToggle: {
    backgroundColor: '#bfbfbf',
    borderColor: '#bfbfbf',
    color: '#fafafa',
  },
  disabledUnselectedToggle: {
    borderColor: '#bfbfbf',
    color: '#bfbfbf',
  },
  firstToggle: {
    borderBottomLeftRadius: TabStyles.Sizing.BorderRadius,
    borderTopLeftRadius: TabStyles.Sizing.BorderRadius,
  },
  lastToggle: {
    borderBottomRightRadius: TabStyles.Sizing.BorderRadius,
    borderTopRightRadius: TabStyles.Sizing.BorderRadius,
  }
};

export class ToggleTextWidget extends React.PureComponent<ToggleTextProps, {}> {
  private handleChange = (idx: number): void => {
    if (idx === 1) {
      this.props.handleRightToggleSelected?.();
    } else {
      this.props.handleLeftToggleSelected?.();
    }
  }

  public render(): JSX.Element {
    return (
      <SwitcherTextBase
        ariaLabel={this.props.ariaLabel}
        className={this.props.className}
        checkedIndex={this.props.selectedToggle === SelectedToggle.Right ? 1 : 0}
        disabled={this.props.disabled}
        handleChange={this.handleChange}
        name={this.props.name}
        style={{ ...SwitcherStyles, container: { ...SwitcherStyles.container, ...this.props.containerStyle } }}
        testId={this.props.testId}
        toggleTexts={[this.props.leftToggleText, this.props.rightToggleText]}
      />
    );
  }
}
